import * as amplitude from '@amplitude/analytics-browser';
import { Experiment, Source } from '@amplitude/experiment-js-client';

import { ArticlePagePropertiesPlugin } from '@/components/Analytics/Plugins/ArticlePagePropertiesPlugin/ArticlePagePropertiesPlugin';
import {
    isMyItemsHubShownAtBoot,
    myItemsHubPagePropertiesPlugin,
} from '@/components/Analytics/Plugins/MyItemsHubPagePropertiesPlugin/MyItemsHubPagePropertiesPlugin';
import { markAmplitudeAnalyticsReady } from '@/utils/amplitude/analytics/amplitudeAnalyticsReady';
import { getAmplitudePageNameFromPath, SEARCH_DRIVEN_PAGE_NAMES } from '@/utils/getPageNameFromPath';

/**
 * Search/category pages' result count isn't known until their results fetch resolves, which
 * happens after the URL has already changed -- so Amplitude's own autocapture (tied to
 * history.pushState) would always fire before the count is ready. `useTrackSearchPageViewed`
 * fires this event manually for those routes once the count is known instead.
 *
 * Exported for testing only -- not meant to be imported elsewhere.
 */
export const isSearchDrivenPath = () =>
    SEARCH_DRIVEN_PAGE_NAMES.includes(getAmplitudePageNameFromPath(window.location.pathname, window.location.search));

export const initializeAmplitudeAnalyticsAndExperimentation = async (
    amplitudeDeploymentKey: string,
    customProperties: {
        hasEverPlacedBid: boolean;
        hasEverRegisteredForAuction: boolean;
    }
) => {
    const serverDeviceId = window.__amplitudeExperiments.deviceId;
    const serverUserId = window.__amplitudeExperiments.userId;

    // Queue before init. Core init flushes this queue before the page-view plugin tracks the current page.
    amplitude.add(new ArticlePagePropertiesPlugin());
    myItemsHubPagePropertiesPlugin.isHubShown = isMyItemsHubShownAtBoot(
        window.__amplitudeExperiments.variants,
        serverUserId
    );
    amplitude.add(myItemsHubPagePropertiesPlugin);

    // initialize amplitude analytics client
    // Options marked "pinned" keep pre-2.29 SDK behaviour — newer defaults change what gets tracked (LAAC-2563).
    // Re-run playwright/e2e/amplitudeParity before changing any of them.
    void amplitude
        .init(amplitudeDeploymentKey, {
            autocapture: {
                // pinned: the default also copies utm/referrer/click ids into every event's properties
                attribution: { trackingMethod: 'userProperty' },
                elementInteractions: false,
                fileDownloads: false,
                formInteractions: false,
                // pinned: the default adds [Amplitude] Page/Previous Page properties to every event
                pageUrlEnrichment: false,
                pageViews: { trackOn: () => !isSearchDrivenPath() },
                sessions: false,
            },
            // pinned: otherwise remote config can turn on server-supplied JS that rewrites events
            customEnrichment: false,

            deviceId: serverDeviceId,
            // pinned: diagnostics requests didn't exist before
            enableDiagnostics: false,
            logLevel: __DEVELOPMENT__ ? amplitude.Types.LogLevel.Warn : amplitude.Types.LogLevel.Error,
            // pinned: remote config can override the autocapture settings above
            remoteConfig: { fetchRemoteConfig: false },
            // pinned: keepalive delivers events fired during navigation, changing counts
            transport: { enableKeepalive: false, type: 'fetch' },
            userId: serverUserId,
        })
        .promise.then(markAmplitudeAnalyticsReady);

    // I don't believe we need this call, but commenting it out to see if it improves load times.
    // // wait for analytics init
    // await amplitudeAnalyticsClient.promise;

    // initialize amplitude experiments client
    const amplitudeExperimentsClient = Experiment.initializeWithAmplitudeAnalytics(amplitudeDeploymentKey, {
        automaticExposureTracking: false,
        automaticFetchOnAmplitudeIdentityChange: true,
        debug: false,
        fetchOnStart: false,
        initialVariants: window.__amplitudeExperiments.variants,
        retryFetchOnFailure: true,
        source: Source.LocalStorage,
        userProvider: {
            getUser: () => {
                return {
                    device_id: serverDeviceId,
                    user_id: serverUserId,
                    user_properties: {
                        'Has Ever Placed Bid': customProperties.hasEverPlacedBid,
                        'Has Ever Registered For Auction': customProperties.hasEverRegisteredForAuction,
                    },
                };
            },
        },
    });

    const client = await amplitudeExperimentsClient.fetch({
        device_id: serverDeviceId,
        user_id: serverUserId,
        user_properties: {
            'Has Ever Placed Bid': customProperties.hasEverPlacedBid,
            'Has Ever Registered For Auction': customProperties.hasEverRegisteredForAuction,
        },
    });

    return {
        amplitudeExperimentsClient: client,
    };
};
